import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.

export const bloodpressureMarcus: Scenario = {
  slug: 'bloodpressure-marcus',
  series: 'Cardiovascular Risk',
  title: "Three blood pressure drugs. Still the wrong target.",
  socialHook:
    "Marcus, 52: four years, three blood pressure medicines, still 145/92. The mystery was not resistant hypertension. It was treatment aimed at the wrong pressure system.",

  meta: {
    description:
      "A fictional case study: Marcus has been on three blood pressure medicines with poor control. His genetics point to low-renin, salt-sensitive hypertension — a pattern that often responds to drug classes he has not been offered.",
  },

  persona: {
    name: 'Marcus',
    age: 52,
    sex: 'Male',
    ancestry: 'Black British / Nigerian heritage',
    occupation: 'Senior civil servant',
    chiefConcern:
      "He is on his third blood pressure medicine in four years. Lisinopril barely helped, amlodipine helped a little, and losartan was added on top. His last reading was still 145/92. He is 52 — the age his father was when stroke started to feel close.",
    familyHistory:
      "Father: hypertension and stroke at age 58. Mother: hypertension, well-controlled on amlodipine.",
  },

  clinicalPicture: {
    symptoms: [
      'No symptoms — hypertension is discovered on routine checks',
      'Occasional headaches, attributed to work stress',
      'Fatigue, especially in the afternoon',
    ],
    labs: [
      { name: 'Blood Pressure', value: '145/92 mmHg (repeated)', refRange: '<130/80 mmHg', flag: 'high' },
      { name: 'Aldosterone:Renin Ratio', value: 'Elevated', refRange: '<30 (units lab-dependent)', flag: 'high' },
    ],
    medications: [
      'Lisinopril (ACE inhibitor) — minimal response',
      'Amlodipine (calcium channel blocker) — partial response',
      'Losartan (ARB) — recently added, BP still 145/92',
    ],
    supplements: ['No regular supplements'],
    lifestyle: [
      'Moderate salt intake — home-cooked Nigerian food, occasional takeaway',
      'Sedentary work — desk-based, minimal formal exercise',
      'Non-smoker',
      'Alcohol: 4–6 drinks per week',
      'BMI 28 (slightly overweight)',
    ],
  },

  genetics: [
    {
      gene: 'AGT',
      variant: 'M235T',
      rsid: 'rs699',
      genotype: 'T allele carrier',
      plainMeaning:
        "Marcus's blood pressure system is set to run high and react strongly to salt. AGT helps explain why his current medicines are fighting uphill.",
      technicalDetail:
        'AGT produces angiotensinogen, the precursor to angiotensin II — a hormone that raises blood pressure. The T allele is associated with constitutively higher angiotensinogen levels and stronger salt-sensitivity; it is more common in people of West African ancestry. ACE inhibitors and ARBs (both of which Marcus takes) target this pathway, but with angiotensinogen elevated at baseline, the feedback system keeps generating more angiotensin II to replace what the drug blocks — which helps explain why his current combination is underperforming.',
    },
    {
      gene: 'ADD1',
      variant: 'Gly460Trp',
      rsid: 'rs4961',
      genotype: 'Trp allele carrier',
      plainMeaning:
        "His kidneys hold on to more salt and water than they should. ADD1 points to a drug class built for this problem, but he has not been prescribed it.",
      technicalDetail:
        'Alpha-adducin affects sodium reabsorption in the kidney tubules. The Trp allele causes the kidneys to retain more sodium — and therefore more water — than average, raising blood pressure through volume. This variant specifically predicts a strong response to thiazide diuretics. Marcus has never been prescribed a thiazide.',
    },
    {
      gene: 'CYP11B2',
      variant: 'Aldosterone synthase promoter',
      rsid: 'rs1799998',
      genotype: 'Risk allele carrier',
      plainMeaning:
        "His body may be sending an extra “keep salt” signal to the kidneys. CYP11B2 fits his lab result, and a medicine that targets this signal has not been tried.",
      technicalDetail:
        "CYP11B2 encodes aldosterone synthase, the enzyme that produces aldosterone — a hormone that tells the kidneys to hold onto sodium. This variant drives higher aldosterone output. His elevated aldosterone:renin ratio is the clinical fingerprint of exactly this pattern. A mineralocorticoid receptor antagonist such as spironolactone would target this mechanism directly. It has not been tried.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: 'The number would not move',
      body: "Marcus has had some version of the same appointment for four years. A medicine is added, the reading improves a little or not at all, and the next visit starts over. Lisinopril barely moved it. Amlodipine helped a little. Losartan was added, and the reading is still 145/92. His father had a stroke at 58. Marcus is 52. This is not an abstract risk to him.",
    },
    {
      type: 'narrative',
      heading: "Resistant was the wrong word",
      body: "Calling it resistant makes it sound as if Marcus's body is impossible to treat. The better question is: which pressure system is actually driving the number? Blood pressure can rise because of vessel tone, kidney salt handling, aldosterone, or the renin-angiotensin system. If salt retention and aldosterone are the drivers, adding more medicines around the same pathway can miss the point. That is not untreatable hypertension. It is hypertension aimed at the wrong targets.",
    },
    {
      type: 'reveal',
      heading: "His first clue was salt-sensitive pressure",
      body: "Marcus's system looks built to hold pressure up, especially when salt is in the picture. His AGT variant is linked with higher angiotensinogen, a starting material for a blood-pressure-raising hormone. Lisinopril and losartan work in that pathway, but for this low-renin, salt-sensitive pattern they may not be the best first targets. Hypertension guidelines for people of Black African ancestry recognize this pattern and often prioritize calcium channel blockers and diuretics. Marcus has been treated as if the main target were somewhere else.",
    },
    {
      type: 'reveal',
      heading: "The bigger clue was in the kidneys",
      body: "Two more findings point to the target no one has fully treated: salt held by the kidneys. ADD1 helps explain why Marcus may retain more sodium and water, raising pressure through volume. CYP11B2 fits the elevated aldosterone:renin ratio, a sign that his body is telling the kidneys to hold even more salt. The targeted options are familiar: a thiazide diuretic for sodium retention and spironolactone for aldosterone excess. Neither has been tried.",
    },
    {
      type: 'action',
      heading: "Five appointment questions that match the target",
      items: [
        "Ask whether the aldosterone:renin ratio has been reviewed. An elevated result points toward aldosterone as a treatment target and makes spironolactone a specific discussion.",
        "Ask about a thiazide diuretic such as indapamide. His ADD1 result points toward sodium retention, and this class has not been tried.",
        "Treat sodium reduction as targeted treatment, not generic advice. His genetics make salt more likely to move his blood pressure, so aiming for under 2 g of sodium per day is a focused experiment.",
        "Ask for a cardiology or hypertension specialist referral. Four years of poor control across three drug classes, plus his father's stroke history, justify a broader medication review.",
        "Name the ancestry and pharmacogenomics issue directly. Guidelines recognize that people of Black African ancestry often respond better to calcium channel blockers and diuretics than ACE inhibitors or ARBs as first-line therapy.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "Why three drugs still missed the target",
      caption:
        "Marcus's current regimen leans on pathways that may not be driving his pressure. These changes aim at salt retention and aldosterone, the mechanisms his profile points toward. This is illustrative, not a medical risk calculator.",
      baselineLabel: "Current BP 145/92 — three drugs, wrong targets",
      baseline: 82,
      levers: [
        {
          label: 'Add thiazide diuretic (ADD1 target)',
          delta: -20,
          description: 'The ADD1 Trp allele predicts strong response to thiazide diuretics. Not yet tried.',
        },
        {
          label: 'Add spironolactone (CYP11B2 / aldosterone target)',
          delta: -18,
          description: 'His elevated aldosterone:renin ratio points directly to spironolactone as an undertreated mechanism.',
        },
        {
          label: 'Reduce dietary sodium (AGT salt-sensitivity)',
          delta: -12,
          description: 'The AGT T allele makes sodium restriction more effective for Marcus than for an average patient.',
        },
        {
          label: 'Specialist-guided medication review',
          delta: -8,
          description: 'A hypertension specialist reviewing four years of inadequate control can restructure the regimen as a whole.',
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'bloodpressure-marcus',
    label: 'Explore your blood pressure genetics in Coach',
  },
}
